// Derive the set of legacy skip_type enum values currently visible to
// the tenant.
//
// Skip jobs / skip enquiries persist `skip_type` as the legacy text
// enum (skip / bin / roro / other). The forms still use that schema,
// but the dropdown options should hide globals whose activation row
// is missing — otherwise the user can pick e.g. "Bin" with no Bin
// global activated and the size dropdown will be empty.
//
// Inventory selectors use the newer enc_id-based stock_type_enc_id
// path; this hook only feeds the legacy-enum surfaces.
//
// Globals seeded by 011_skip_stock_defaults.sql:
//   prefix SKP → 'skip'
//   prefix BIN → 'bin'
//   prefix ROR → 'roro'
//
// The 'other' option is generic (catch-all for own-tenant types or
// unmapped historical data) and is always offered separately by the
// caller — it is not derived here.

import { useMemo } from 'react';

import { useSkipStockTypes } from '../settings/hooks/useSkipStock';

export type LegacySkipType = 'skip' | 'bin' | 'roro';

const PREFIX_TO_LEGACY: Readonly<Record<string, LegacySkipType>> = {
  SKP: 'skip',
  BIN: 'bin',
  ROR: 'roro',
};

export interface CustomStockType {
  readonly enc_id: string;
  /** Stable uuid — used by the form size dropdown to match against
   *  SkipSize.stock_type_uuid. The enc_id alone can't be used
   *  because encryptField is non-deterministic across responses. */
  readonly uuid: string;
  readonly name: string;
  readonly prefix: string;
}

interface ActivatedLegacySkipTypes {
  /** Loading state surfaced from the underlying React Query. The form
   *  should keep its existing skeleton/loading affordance — typically
   *  the dropdown stays empty until the data lands. */
  readonly isLoading: boolean;
  /** Distinct legacy skip_type values whose corresponding global is
   *  activated for this tenant, in stable display order (skip, bin,
   *  roro). */
  readonly types: ReadonlyArray<LegacySkipType>;
  /** True when the tenant has at least one own-tenant (custom) stock
   *  type. Callers append a generic 'other' option in addition to
   *  legacy types — the boolean is exposed so they can decide whether
   *  it's the *only* option to show beyond legacy. */
  readonly hasCustomTypes: boolean;
  /** Bug 2 — own-tenant custom stock types (company_id IS NOT NULL),
   *  emitted alongside the legacy globals so the form Skip Type
   *  dropdown can render them as first-class options. Callers map
   *  selecting a custom to skip_type='other' on the persisted row
   *  AND track the custom's enc_id in form state to filter the size
   *  dropdown (see SkipJob/EnquiryCreatePage's size filter). */
  readonly customTypes: ReadonlyArray<CustomStockType>;
}

const LEGACY_ORDER: ReadonlyArray<LegacySkipType> = ['skip', 'bin', 'roro'];

export function useActivatedLegacySkipTypes(): ActivatedLegacySkipTypes {
  const typesQuery = useSkipStockTypes({ forSelection: true });
  return useMemo(() => {
    const stockTypes = typesQuery.data?.stockTypes ?? [];
    const activatedLegacy = new Set<LegacySkipType>();
    const customs: CustomStockType[] = [];
    for (const t of stockTypes) {
      if (t.is_global) {
        const legacy = PREFIX_TO_LEGACY[t.prefix.toUpperCase()];
        if (legacy) activatedLegacy.add(legacy);
      } else {
        customs.push({ enc_id: t.enc_id, uuid: t.uuid, name: t.name, prefix: t.prefix });
      }
    }
    return {
      isLoading: typesQuery.isLoading,
      types: LEGACY_ORDER.filter((t) => activatedLegacy.has(t)),
      hasCustomTypes: customs.length > 0,
      customTypes: customs,
    };
  }, [typesQuery.data, typesQuery.isLoading]);
}
